/**
 * World chat — the bottom-left panel.
 *
 * Janeks: *"Under character section button - chat, toggles bottom left chat,
 * slightly opaque background, bots chatting. Can be scrolled. Player can chat
 * too. simulate real talk. Do not react to players messages. Show message
 * time."*
 *
 * ─── Not a panel, on purpose ────────────────────────────────────────────────
 * Every other window in this game is a full-screen scrim at z-index 1001 that
 * takes the whole screen and blocks the HUD underneath. Chat cannot be one:
 * you read chat WHILE walking around, which is the entire point of it being a
 * corner panel rather than a screen. So it is a bounded box with its own
 * z-index above the scrims, and `pointer-events` is granted only to the parts
 * that need it — the scroller and the input — so the canvas behind the rest of
 * the bottom-left corner still receives clicks.
 *
 * ─── The autoscroll rule ────────────────────────────────────────────────────
 * New lines scroll into view ONLY when the reader is already at the bottom.
 * Scrolling up to read something and being yanked back down by a bot saying
 * "gg" is the single most irritating thing a chat window can do, and with a
 * population of 400 it would happen constantly.
 *
 * ─── Typing must not walk the player ────────────────────────────────────────
 * `index.ts` listens for keydown on `window`, so every character typed here
 * would otherwise also be a movement key — "wts scrap" would walk the player
 * across the settlement. The guard lives at the top of that handler
 * (`isTypingTarget`), not here, because `player-controls.ts` and the action-bar
 * shortcuts read the same event.
 */
import {
  chatStateStore, closeChat, formatMessageTime, openChat, sendChat,
} from '../../state/chat-state';
import type { ChatMessageView } from '../../state/chat-state.types';
import { closeButtonHtml } from './close-button';

/** `ChatService` (core) caps a message at 500 characters and rejects anything longer. */
const MAX_LENGTH = 500;

export interface ChatUI {
  readonly el: HTMLElement;
  dispose(): void;
}

export function isChatOpen(): boolean {
  return chatStateStore.get().open;
}

export function toggleChatUI(): void {
  if (isChatOpen()) closeChat();
  else openChat();
}

export function createChatUI(): ChatUI {
  const el = document.createElement('div');
  el.className = 'bv-chat';
  el.innerHTML = `
    <div class="bv-chat-head">
      <span class="bv-chat-title">World</span>
      ${closeButtonHtml({ label: 'Close chat', className: 'bv-chat-close bv-close-inline', attrs: 'data-close title="Close chat (T)"' })}
    </div>
    <div class="bv-chat-log" data-log role="log" aria-live="polite"></div>
    <form class="bv-chat-form" data-form>
      <input class="bv-chat-input" data-input type="text" maxlength="${MAX_LENGTH}"
             placeholder="Say something…" autocomplete="off" aria-label="World chat message">
      <button type="submit" class="bv-chat-send" data-send>Send</button>
    </form>`;

  const logEl = el.querySelector('[data-log]') as HTMLElement;
  const formEl = el.querySelector('[data-form]') as HTMLFormElement;
  const inputEl = el.querySelector('[data-input]') as HTMLInputElement;

  (el.querySelector('[data-close]') as HTMLButtonElement).addEventListener('click', () => closeChat());

  formEl.addEventListener('submit', (e) => {
    e.preventDefault();
    const value = inputEl.value;
    inputEl.value = '';
    sendChat(value);
    // Focus is kept: a conversation is usually more than one line, and
    // re-clicking the box for every message is worse than holding it.
    inputEl.focus();
  });

  // Escape gets out of the input without closing the window — the player
  // wants their movement keys back, not necessarily to stop reading.
  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      inputEl.blur();
    }
  });

  /** Rendered ids, so a repaint appends rather than rebuilding the scrollback. */
  const rendered = new Set<string>();

  function atBottom(): boolean {
    // A few pixels of slack: sub-pixel scroll heights mean an exact equality
    // test reads as "scrolled up" at the actual bottom.
    return logEl.scrollHeight - logEl.scrollTop - logEl.clientHeight < 24;
  }

  function rowHtml(m: ChatMessageView): string {
    return `<div class="bv-chat-row${m.self ? ' bv-chat-row-self' : ''}" data-id="${esc(m.id)}">`
      + `<span class="bv-chat-time">${esc(formatMessageTime(m.createdAt))}</span>`
      + `<span class="bv-chat-who">${esc(m.username)}</span>`
      + `<span class="bv-chat-text">${esc(m.content)}</span>`
      + '</div>';
  }

  const unsub = chatStateStore.subscribe((state) => {
    el.classList.toggle('bv-chat-open', state.open);
    el.hidden = !state.open;
    if (!state.open) return;

    const stick = atBottom();

    // The store caps its buffer, so a long session drops old lines — a full
    // rebuild would be the simple answer but it destroys the selection and the
    // scroll position on every incoming message. Append what is new, drop what
    // has aged out.
    const live = new Set(state.messages.map((m) => m.id));
    for (const id of [...rendered]) {
      if (live.has(id)) continue;
      logEl.querySelector(`[data-id="${CSS.escape(id)}"]`)?.remove();
      rendered.delete(id);
    }
    const fresh = state.messages.filter((m) => !rendered.has(m.id));
    if (fresh.length > 0) {
      logEl.insertAdjacentHTML('beforeend', fresh.map(rowHtml).join(''));
      for (const m of fresh) rendered.add(m.id);
    }

    if (stick) logEl.scrollTop = logEl.scrollHeight;
  });

  return {
    el,
    dispose(): void {
      unsub();
      el.remove();
    },
  };
}

/**
 * True when the event's target is a text field.
 *
 * Exported because `index.ts`'s `window` keydown is where it has to be
 * applied — see this file's header. Kept next to the chat because the chat
 * input is the only text field in the base view, so this is where anyone
 * looking for the reason will come.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
}

/** Everything interpolated here is user-supplied chat text. */
function esc(value: string): string {
  return value.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}
